import { cn } from '@/lib/utils';
import { Check, Pencil, X } from 'lucide-react';

/**
 * Visual variant per box — drives the surrounding chip colors. Kept
 * narrow so we don't accidentally introduce new variants that don't
 * map to brand tokens.
 */
type Variant = 'neutral' | 'proposed' | 'counter';

interface EditableBoxProps {
  /** UPPERCASE label shown above the value (e.g. "Original"). */
  label: string;
  /** Current read-only value. */
  value: string;
  /** When true, the box is in edit mode and renders a textarea. */
  isEditing: boolean;
  /** Live draft text while the user is typing (only used in edit mode). */
  draft: string;
  /** Visual styling preset — see `Variant` above. */
  variant: Variant;
  onStartEdit: () => void;
  onDraftChange: (next: string) => void;
  onSave: () => void;
  onCancel: () => void;
  /** Translated labels — passed in so the parent can resolve them once. */
  editLabel: string;
  saveLabel: string;
  cancelLabel: string;
}

const CONTAINER_STYLE: Record<Variant, string> = {
  neutral: 'bg-muted/60 border-border',
  proposed: 'bg-selise-blue/10 border-selise-blue-500/30',
  counter: 'bg-card border-border',
};

const LABEL_STYLE: Record<Variant, string> = {
  neutral: 'text-globe-grey',
  proposed: 'text-selise-blue-500',
  counter: 'text-globe-grey',
};

const TEXT_STYLE: Record<Variant, string> = {
  neutral: 'text-globe-grey italic',
  proposed: 'font-semibold text-selise-blue-500',
  counter: 'text-sm text-[hsl(var(--fg))]',
};

/**
 * A read-only labeled text box with a top-right pencil button. When
 * clicked, the box swaps the static text for a textarea + Save /
 * Cancel actions. Used twice per clause (Proposed Change + Full
 * Counter-Clause) and once in read-only form (Original — pencil
 * suppressed by simply not rendering this component there).
 *
 * Keyboard shortcuts while editing: `Esc` cancels, `Ctrl/Cmd + Enter`
 * saves. The parent owns the editing state (which box is editing +
 * the current draft) so only one cell is ever active at a time.
 */
export function EditableBox({
  label,
  value,
  isEditing,
  draft,
  variant,
  onStartEdit,
  onDraftChange,
  onSave,
  onCancel,
  editLabel,
  saveLabel,
  cancelLabel,
}: EditableBoxProps) {
  return (
    <div className={cn('relative p-3 border rounded', CONTAINER_STYLE[variant])}>
      {!isEditing && (
        <button
          type="button"
          onClick={onStartEdit}
          aria-label={editLabel}
          title={editLabel}
          className="absolute top-1.5 right-1.5 p-1 rounded text-globe-grey hover:text-selise-blue hover:bg-card/80 transition-colors"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
      )}
      <p
        className={cn(
          'text-[10px] font-semibold uppercase tracking-wider mb-1 pr-6',
          LABEL_STYLE[variant]
        )}
      >
        {label}
      </p>
      {isEditing ? (
        <EditableBoxEditor
          draft={draft}
          variant={variant}
          onDraftChange={onDraftChange}
          onSave={onSave}
          onCancel={onCancel}
          saveLabel={saveLabel}
          cancelLabel={cancelLabel}
        />
      ) : (
        <p className={cn('wrap-break-word pr-6', TEXT_STYLE[variant])}>{value}</p>
      )}
    </div>
  );
}

interface EditableBoxEditorProps {
  draft: string;
  variant: Variant;
  onDraftChange: (next: string) => void;
  onSave: () => void;
  onCancel: () => void;
  saveLabel: string;
  cancelLabel: string;
}

/** Inline edit-mode UI — textarea + Save / Cancel pair. */
function EditableBoxEditor({
  draft,
  variant,
  onDraftChange,
  onSave,
  onCancel,
  saveLabel,
  cancelLabel,
}: EditableBoxEditorProps) {
  return (
    <>
      <textarea
        value={draft}
        onChange={(event) => onDraftChange(event.target.value)}
        autoFocus
        rows={3}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            onCancel();
          } else if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
            event.preventDefault();
            onSave();
          }
        }}
        className={cn(
          'w-full rounded border border-border bg-card px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selise-blue/40 resize-y min-h-16',
          variant === 'proposed' && 'text-selise-blue font-semibold'
        )}
      />
      <div className="flex justify-end gap-2 mt-2">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold text-globe-grey hover:bg-muted"
        >
          <X className="w-3.5 h-3.5" />
          {cancelLabel}
        </button>
        <button
          type="button"
          onClick={onSave}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold text-white bg-selise-blue hover:bg-oxford-blue"
        >
          <Check className="w-3.5 h-3.5" />
          {saveLabel}
        </button>
      </div>
    </>
  );
}

export default EditableBox;
